import React, { useEffect, useState } from 'react';
import { Plus, PencilSimple, Trash, X, FloppyDisk, CircleNotch, Drop, Gauge } from '@phosphor-icons/react';
import axios from '../../utils/axiosInstance';
import { vehicleLabel } from '../../utils/vehicleLabel';
import PrzebiegiTab from './PrzebiegiTab';
import { WlasnoscFilter, WlasnoscBadge, normalizeWlasnosc } from '../shared/WlasnoscFilter';

const API = `${window.location.origin}/api`;

const EMPTY_FORM = {
    pojazd_id: '',
    data: new Date().toISOString().slice(0, 10),
    przebieg: '',
    notes: '',
};

export default function WymianaOlejuTab() {
    const [subTab, setSubTab] = useState('historia'); // 'historia' | 'przebiegi'
    const [items, setItems] = useState([]);
    const [pojazdy, setPojazdy] = useState([]);
    const [przebiegStatus, setPrzebiegStatus] = useState([]); // from /api/przebiegi/status
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [filterPojazd, setFilterPojazd] = useState('');
    const [wlasnoscFilter, setWlasnoscFilter] = useState('all');

    // pojazd_id → wlasnosc lookup
    const pojazdWlasnoscById = React.useMemo(() => {
        const m = {};
        for (const p of pojazdy) m[p.id] = normalizeWlasnosc(p.wlasnosc);
        return m;
    }, [pojazdy]);

    // pojazd_id → latest przebieg (km) from employee entries, so the "Nowa
    // wymiana oleju" modal auto-fills with the most recent reading.
    const latestKmByPojazdId = React.useMemo(() => {
        const m = {};
        for (const s of przebiegStatus) {
            if (s?.pojazd_id && s.last_km != null) m[s.pojazd_id] = s.last_km;
        }
        return m;
    }, [przebiegStatus]);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [w, p, s] = await Promise.all([
                axios.get(`${API}/wymiana-oleju`),
                axios.get(`${API}/pojazdy`),
                axios.get(`${API}/przebiegi/status`).catch(() => ({ data: [] })),
            ]);
            setItems(w.data || []);
            setPojazdy(p.data || []);
            setPrzebiegStatus(s.data || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, []);

    const openNew = () => {
        setEditing(null);
        setForm({ ...EMPTY_FORM });
        setShowModal(true);
    };

    const openEdit = (item) => {
        setEditing(item);
        setForm({
            pojazd_id: item.pojazd_id || '',
            data: item.data || '',
            przebieg: item.przebieg != null ? String(item.przebieg) : '',
            notes: item.notes || '',
        });
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!form.pojazd_id) return alert('Wybierz pojazd');
        if (!form.data) return alert('Podaj datę');
        const km = parseFloat(form.przebieg);
        if (isNaN(km) || km < 0) return alert('Podaj poprawny przebieg (km)');
        setSaving(true);
        try {
            const payload = {
                pojazd_id: form.pojazd_id,
                data: form.data,
                przebieg: km,
                notes: form.notes || null,
            };
            if (editing) {
                await axios.put(`${API}/wymiana-oleju/${editing.id}`, payload);
            } else {
                await axios.post(`${API}/wymiana-oleju`, payload);
            }
            setShowModal(false);
            setEditing(null);
            setForm(EMPTY_FORM);
            fetchAll();
        } catch (err) {
            alert(err.response?.data?.detail || 'Błąd zapisu');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Usunąć ten wpis?')) return;
        try {
            await axios.delete(`${API}/wymiana-oleju/${id}`);
            fetchAll();
        } catch (err) {
            alert(err.response?.data?.detail || 'Błąd usuwania');
        }
    };

    const pojazdLabel = (p) => vehicleLabel(p);

    const filtered = React.useMemo(() => {
        let arr = filterPojazd ? items.filter(x => x.pojazd_id === filterPojazd) : items;
        if (wlasnoscFilter !== 'all') {
            arr = arr.filter(x => (pojazdWlasnoscById[x.pojazd_id] || 'firmowy') === wlasnoscFilter);
        }
        return arr;
    }, [items, filterPojazd, wlasnoscFilter, pojazdWlasnoscById]);

    if (loading) {
        return <div className="text-center py-12"><CircleNotch className="w-8 h-8 animate-spin mx-auto text-muted-foreground" /></div>;
    }

    return (
        <div>
            {/* Sub-tabs: Historia wymian | Przebiegi (monthly mileage) */}
            <div className="flex gap-1 border-b border-slate-200 mb-4">
                <button
                    onClick={() => setSubTab('historia')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 ${
                        subTab === 'historia'
                            ? 'border-amber-600 text-amber-700'
                            : 'border-transparent text-slate-500 hover:text-slate-800'
                    }`}
                    data-testid="subtab-historia"
                >
                    <Drop className="w-4 h-4" weight={subTab === 'historia' ? 'fill' : 'regular'} />
                    Historia wymian
                </button>
                <button
                    onClick={() => setSubTab('przebiegi')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 ${
                        subTab === 'przebiegi'
                            ? 'border-amber-600 text-amber-700'
                            : 'border-transparent text-slate-500 hover:text-slate-800'
                    }`}
                    data-testid="subtab-przebiegi"
                >
                    <Gauge className="w-4 h-4" weight={subTab === 'przebiegi' ? 'fill' : 'regular'} />
                    Przebiegi
                </button>
            </div>

            {subTab === 'przebiegi' && <PrzebiegiTab pojazdy={pojazdy} />}
            {subTab === 'historia' && (
            <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div>
                    <h2 className="font-heading text-lg sm:text-xl font-bold flex items-center gap-2">
                        <Drop className="w-5 h-5 text-amber-600" weight="bold" /> Wymiana oleju
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1">Historia wymian oleju — pojazd, data i przebieg w momencie wymiany.</p>
                </div>
                <button onClick={openNew} className="btn-primary flex items-center gap-2 text-sm" data-testid="wymiana-add-btn">
                    <Plus className="w-4 h-4" /> Dodaj wymianę
                </button>
            </div>

            <div className="mb-3 flex flex-wrap items-center gap-3">
                <select value={filterPojazd} onChange={(e) => setFilterPojazd(e.target.value)} className="text-sm px-3 py-2 border border-slate-300 rounded-sm">
                    <option value="">Wszystkie pojazdy ({items.length})</option>
                    <optgroup label="Firmowe">
                        {pojazdy.filter(p => normalizeWlasnosc(p.wlasnosc) === 'firmowy').map(p => <option key={p.id} value={p.id}>{pojazdLabel(p)}</option>)}
                    </optgroup>
                    <optgroup label="Prywatne">
                        {pojazdy.filter(p => normalizeWlasnosc(p.wlasnosc) === 'prywatny').map(p => <option key={p.id} value={p.id}>{pojazdLabel(p)}</option>)}
                    </optgroup>
                </select>
                <WlasnoscFilter value={wlasnoscFilter} onChange={setWlasnoscFilter} />
            </div>

            {filtered.length === 0 ? (
                <div className="text-center py-16 text-slate-500 bg-white border border-slate-200 rounded-sm">
                    Brak wpisów. Dodaj pierwszą wymianę!
                </div>
            ) : (
                <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto -mx-3 sm:mx-0">
                    <table className="w-full text-sm min-w-[700px]">
                        <thead className="bg-muted">
                            <tr className="text-left text-xs font-semibold text-slate-600 uppercase">
                                <th className="px-4 py-3">Data</th>
                                <th className="px-4 py-3">Pojazd</th>
                                <th className="px-4 py-3 text-right">Przebieg (km)</th>
                                <th className="px-4 py-3">Uwagi</th>
                                <th className="px-4 py-3 text-right">Akcje</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filtered.map(w => (
                                <tr key={w.id} className="hover:bg-slate-50" data-testid={`wymiana-row-${w.id}`}>
                                    <td className="px-4 py-3 font-medium text-slate-800">{w.data}</td>
                                    <td className="px-4 py-3 text-slate-700 text-xs">
                                        <div className="flex items-center gap-1.5">
                                            <span>{pojazdLabel(w.pojazd)}</span>
                                            <WlasnoscBadge value={pojazdWlasnoscById[w.pojazd_id]} size="xs" />
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono font-semibold">{Number(w.przebieg || 0).toLocaleString('pl-PL')} km</td>
                                    <td className="px-4 py-3 text-slate-600 text-xs">{w.notes || '—'}</td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                        <button onClick={() => openEdit(w)} className="p-1.5 hover:bg-slate-100 rounded-sm" data-testid={`wymiana-edit-${w.id}`}><PencilSimple className="w-4 h-4" /></button>
                                        <button onClick={() => handleDelete(w.id)} className="p-1.5 hover:bg-red-50 text-red-600 rounded-sm ml-1" data-testid={`wymiana-delete-${w.id}`}><Trash className="w-4 h-4" /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-sm w-full max-w-md p-6" data-testid="wymiana-modal">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-heading text-xl font-bold">{editing ? 'Edytuj wymianę oleju' : 'Nowa wymiana oleju'}</h3>
                            <button onClick={() => setShowModal(false)} className="p-2 hover:bg-muted rounded-sm"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="field-label block mb-2">Pojazd *</label>
                                <select value={form.pojazd_id} onChange={(e) => {
                                    const nextPojazdId = e.target.value;
                                    setForm(p => {
                                        // Auto-fill przebieg from the latest employee reading
                                        // for the newly-selected vehicle, but only when:
                                        //  - adding a new wymiana (not editing)
                                        //  - the field is empty or still holds the previous
                                        //    vehicle's auto-fill (not a value typed by hand)
                                        const latest = latestKmByPojazdId[nextPojazdId];
                                        const prevLatest = latestKmByPojazdId[p.pojazd_id];
                                        const prevIsAuto = !p.przebieg || (
                                            prevLatest != null &&
                                            String(Math.floor(prevLatest)) === String(p.przebieg).trim()
                                        );
                                        const nextPrzebieg = (!editing && prevIsAuto && latest != null)
                                            ? String(Math.floor(latest))
                                            : p.przebieg;
                                        return { ...p, pojazd_id: nextPojazdId, przebieg: nextPrzebieg };
                                    });
                                }} className="input-field" data-testid="wymiana-pojazd-input">
                                    <option value="">— Wybierz pojazd —</option>
                                    <optgroup label="Firmowe">
                                        {pojazdy.filter(p => normalizeWlasnosc(p.wlasnosc) === 'firmowy').map(p => <option key={p.id} value={p.id}>{pojazdLabel(p)}</option>)}
                                    </optgroup>
                                    <optgroup label="Prywatne">
                                        {pojazdy.filter(p => normalizeWlasnosc(p.wlasnosc) === 'prywatny').map(p => <option key={p.id} value={p.id}>{pojazdLabel(p)}</option>)}
                                    </optgroup>
                                </select>
                            </div>
                            <div>
                                <label className="field-label block mb-2">Data wymiany *</label>
                                <input type="date" value={form.data} onChange={(e) => setForm(p => ({ ...p, data: e.target.value }))} className="input-field" data-testid="wymiana-date-input" />
                            </div>
                            <div>
                                <label className="field-label block mb-2">
                                    Przebieg (km) *
                                    {!editing && form.pojazd_id && latestKmByPojazdId[form.pojazd_id] != null && (
                                        <span className="ml-2 text-xs font-normal text-emerald-700">
                                            — ostatni wpis pracownika: {Math.floor(latestKmByPojazdId[form.pojazd_id]).toLocaleString('pl-PL')} km
                                        </span>
                                    )}
                                </label>
                                <input type="number" step="1" min="0" value={form.przebieg} onChange={(e) => setForm(p => ({ ...p, przebieg: e.target.value }))} placeholder="np. 145000" className="input-field" data-testid="wymiana-przebieg-input" />
                            </div>
                            <div>
                                <label className="field-label block mb-2">Uwagi</label>
                                <textarea value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} className="input-field" data-testid="wymiana-notes-input" placeholder="np. Filtr wymieniony, olej Castrol 5W-30" />
                            </div>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button onClick={() => setShowModal(false)} className="btn-secondary flex-1">Anuluj</button>
                            <button onClick={handleSave} disabled={saving} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="wymiana-save-btn">
                                {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : <FloppyDisk className="w-4 h-4" />} Zapisz
                            </button>
                        </div>
                    </div>
                </div>
            )}
            </>
            )}
        </div>
    );
}
